/**
 * 货物类型（品名）基础资料前端接口
 * @author dream.cai
 */

import { httpClient as request } from '@/utils/http'

// ==================== 类型定义 ====================

/**
 * 启用状态枚举
 */
export enum EnabledStatus {
  /** 启用 */
  ENABLED = 1,
  /** 禁用 */
  DISABLED = 0
}

/**
 * 货物类型响应模型
 */
export interface CargoTypeModel {
  /** 唯一标识 */
  id: string;
  /** 租户ID */
  tenantId?: string;
  /** 货物类型代码 */
  code: string;
  /** 中文名称 */
  nameCn: string;
  /** 英文名称 */
  nameEn?: string;
  /** 排序 */
  sortOrder?: number;
  /** 启用状态：1-启用, 0-禁用 */
  status: number;
  /** 备注 */
  remark?: string;
  /** 是否为平台数据 */
  isPlatformData?: boolean;
  /** 创建时间 */
  dateCreated?: string;
  /** 更新时间 */
  dateModified?: string;
}

/**
 * 货物类型查询请求模型
 */
export interface CargoTypeQueryRequest {
  /** 当前页码，默认为1 */
  pageNum?: number;
  /** 每页大小，默认为10 */
  pageSize?: number;
  /** 关键词，模糊匹配代码/名称 */
  keyword?: string;
  /** 启用状态：1-启用, 0-禁用 */
  status?: number;
  /** 租户ID（一般不传，后端从上下文获取） */
  tenantId?: string;
}

/**
 * 货物类型保存请求模型
 */
export interface CargoTypeSaveRequest {
  /** 主键ID，更新时传递 */
  id?: string;
  /** 货物类型代码 */
  code: string;
  /** 中文名称 */
  nameCn: string;
  /** 英文名称 */
  nameEn?: string;
  /** 排序 */
  sortOrder?: number;
  /** 启用状态：1-启用, 0-禁用，默认为1 */
  status?: number;
  /** 备注 */
  remark?: string;
}

/**
 * 分页响应模型
 */
export interface PageResponse<T> {
  /** 当前页码 */
  current: number;
  /** 每页大小 */
  size: number;
  /** 总记录数 */
  total: number;
  /** 数据列表 */
  records: T[];
}

// ==================== API接口 ====================

/**
 * 货物类型管理API类
 */
export class CargoTypeApi {
  private static readonly BASE_URL = '/mdm/cargoType'

  /**
   * 分页查询货物类型列表
   */
  static async queryPage(params: CargoTypeQueryRequest): Promise<PageResponse<CargoTypeModel>> {
    return request.post(`${CargoTypeApi.BASE_URL}/queryPage`, params)
  }

  /**
   * 查询货物类型列表（不分页）
   */
  static async queryList(params: CargoTypeQueryRequest): Promise<CargoTypeModel[]> {
    return request.post(`${CargoTypeApi.BASE_URL}/queryList`, params)
  }

  /**
   * 根据ID获取货物类型详情
   */
  static async getById(id: string): Promise<CargoTypeModel> {
    return request.post(`${CargoTypeApi.BASE_URL}/getById`, { id })
  }

  /**
   * 保存货物类型（新增或更新）
   */
  static async save(params: CargoTypeSaveRequest): Promise<boolean> {
    return request.post(`${CargoTypeApi.BASE_URL}/save`, params)
  }

  /**
   * 删除货物类型
   */
  static async delete(id: string): Promise<boolean> {
    return request.post(`${CargoTypeApi.BASE_URL}/delete`, { id })
  }

  /**
   * 更新货物类型状态
   */
  static async updateStatus(id: string, status: number): Promise<boolean> {
    return request.post(`${CargoTypeApi.BASE_URL}/updateStatus`, { id, status })
  }
}

// 服务别名（与页面用法一致）
export const CargoTypeService = CargoTypeApi

export default CargoTypeApi
